import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useFormularioDinamico } from '../../../../hooks/useFormularioDinamico';

// ==============================================================
// COMPONENTE DE FIRMA (Reutilizable)
// ==============================================================
function FirmaPad({ onSave, onClear, titulo = "Firma del Responsable" }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#1e293b";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div className="flex flex-col items-center gap-2 w-full mt-2">
      <div className="bg-white/60 backdrop-blur-md border border-slate-300 rounded-2xl shadow-inner overflow-hidden w-full h-[140px] relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute top-2 left-3 text-[9px] font-black text-slate-400 uppercase tracking-widest pointer-events-none select-none">
          {titulo}
        </div>
      </div>
      <button onClick={handleClear} type="button" className="text-[9px] uppercase tracking-wider font-black text-slate-500 hover:text-cyan-600 transition-colors cursor-pointer bg-white/50 px-3 py-1 rounded-full border border-slate-200 shadow-sm">
        Limpiar Firma
      </button>
    </div>
  );
}

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

export default function FormatoLimpiezaDesinfeccion({ usuario, onBack }) {
  // Conectamos con el motor lógico estandarizado y ABAC
  const {
    formulario, respuestas: datosServer, loading, enviando, sedes,
    handleChange: handleServerChange, handleSubmit
  } = useFormularioDinamico('limpieza-desinfeccion', usuario);

  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });

  // Valores iniciales
  useEffect(() => {
    if (!datosServer.fechaEvento) handleServerChange('fechaEvento', new Date().toISOString().split('T')[0]);
    if (!datosServer.responsable && usuario?.terc_nom) {
      handleServerChange('responsable', usuario.terc_nom);
    }
  }, [datosServer.fechaEvento, datosServer.responsable, usuario, handleServerChange]);

  // Extraer el catálogo dinámico de áreas desde la configuración del campo en la BD
  const campoChecklist = formulario?.campos?.find(c => c.nombre_campo === 'respuestas_checklist');
  const categoriasLimpieza = campoChecklist?.opciones?.categorias || [];

  const respuestasChecklist = datosServer.respuestas_checklist || {};

  const setRespuestaItem = (id, valor) => {
    const nuevasRespuestas = { ...respuestasChecklist, [id]: valor };
    handleServerChange('respuestas_checklist', nuevasRespuestas);
  };

  const marcarTodoSi = () => {
    const nuevasRespuestas = {};
    categoriasLimpieza.forEach(cat => {
      cat.items.forEach(item => { nuevasRespuestas[item.id] = 'SI'; });
    });
    handleServerChange('respuestas_checklist', nuevasRespuestas);
  };

  const guardarLocal = (e) => {
    if (!datosServer.pdv || !datosServer.responsable) {
      return setCustomAlert({ show: true, message: "Por favor, completa Sede y Responsable.", type: "warning" });
    }
    if (Object.keys(respuestasChecklist).length === 0) {
      return setCustomAlert({ show: true, message: "Debes diligenciar el checklist antes de guardar.", type: "warning" });
    }
    if (!datosServer.firma_responsable) {
      return setCustomAlert({ show: true, message: "La firma del responsable es obligatoria.", type: "warning" });
    }

    handleSubmit(e, () => {
      setCustomAlert({ show: true, message: "Registro diario de limpieza guardado exitosamente.", type: "success" });
      setTimeout(() => onBack && onBack(), 1500);
    });
  };

  if (loading || !formulario) return <div className="p-10 text-center text-white font-bold">Cargando Limpieza y Desinfección...</div>;

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      {/* CABECERA CYAN / SKY BLUE */}
      <div className="bg-gradient-to-r from-cyan-600/90 to-blue-800/90 border-b border-white/20 px-6 py-4 z-40 flex items-center shadow-md">
        <button onClick={onBack} className="mr-4 w-10 h-10 flex-none bg-white/20 hover:bg-white/40 text-white flex items-center justify-center rounded-full transition-all border border-white/40 shadow-sm cursor-pointer">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <div>
          <h1 className="text-lg sm:text-xl font-black text-white leading-tight">{formulario.titulo}</h1>
          <span className="bg-white/20 text-white border border-white/30 text-[10px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block">CÓDIGO: FT-SST-08 | Versión: {formulario.version}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 relative custom-scrollbar">
        
        {/* DATOS BÁSICOS */}
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-sm font-black text-cyan-900 uppercase tracking-tight flex items-center gap-2">
              <span>📅</span> Información del Día
            </h2>
            <button type="button" onClick={marcarTodoSi} className="bg-emerald-500 hover:bg-emerald-400 text-white text-[10px] font-black px-4 py-2 rounded-xl shadow-sm transition-transform active:scale-95 uppercase tracking-wide cursor-pointer">
              ✓ Marcar todo SÍ
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Fecha de Inspección *</label>
              <input type="date" name="fechaEvento" value={datosServer.fechaEvento || ""} onChange={(e) => handleServerChange('fechaEvento', e.target.value)} className="w-full h-[40px] bg-white/80 border-transparent focus:border-cyan-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm transition-all m-0" />
            </div>
            
            {/* SEDE DINÁMICA FILTRADA POR ABAC */}
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Sede (PDV) *</label>
              <select name="pdv" value={datosServer.pdv || ""} onChange={(e) => handleServerChange('pdv', e.target.value)} className="w-full h-[40px] bg-white/80 border-transparent focus:border-cyan-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm cursor-pointer translate-y-[2px] m-0">
                <option value="" disabled>-- Seleccione una sede --</option>
                {sedes.map(sede => (
                  <option key={sede.id} value={sede.nombre}>
                    {sede.nombre} {sede.codigo_bodega ? `(Bodega: ${sede.codigo_bodega})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Diligenciado Por *</label>
              <input type="text" name="responsable" value={datosServer.responsable || ""} onChange={(e) => handleServerChange('responsable', e.target.value)} className="w-full h-[40px] bg-white/80 border-transparent focus:border-cyan-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm m-0" />
            </div>
          </div>
        </div>

        {/* MEGA CHECKLIST ÁREA POR ÁREA (DINÁMICO DESDE BD) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {categoriasLimpieza.map((cat, i) => (
            <div key={i} className="bg-white/60 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/80">
              <h3 className="text-[11px] font-black text-cyan-800 mb-4 border-b border-cyan-200/50 pb-2">{cat.area}</h3>
              <div className="space-y-3">
                {cat.items.map(item => (
                  <div key={item.id} className="flex justify-between items-center bg-white/50 p-2.5 rounded-xl border border-slate-100">
                    <span className="text-xs font-bold text-slate-700">{item.nombre}</span>
                    <div className="flex gap-1 bg-slate-200/50 p-1 rounded-lg">
                      <button type="button" onClick={() => setRespuestaItem(item.id, 'SI')} className={`px-3 py-1 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestasChecklist[item.id] === 'SI' ? 'bg-emerald-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white'}`}>SI</button>
                      <button type="button" onClick={() => setRespuestaItem(item.id, 'NO')} className={`px-3 py-1 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestasChecklist[item.id] === 'NO' ? 'bg-red-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white'}`}>NO</button>
                      <button type="button" onClick={() => setRespuestaItem(item.id, 'N/A')} className={`px-2 py-1 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestasChecklist[item.id] === 'N/A' ? 'bg-slate-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white'}`}>N/A</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* VALIDACIÓN Y CIERRE */}
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-[10px] font-black text-slate-700 mb-2 uppercase">Observaciones Generales</label>
            <textarea name="observaciones" value={datosServer.observaciones || ""} onChange={(e) => handleServerChange('observaciones', e.target.value)} rows="4" className="w-full bg-white/80 border border-slate-300 focus:border-cyan-400 rounded-xl px-4 py-3 text-sm font-medium text-slate-800 shadow-sm resize-none" />
          </div>
          <div>
            <h2 className="text-sm font-black text-cyan-900 uppercase tracking-tight mb-3">✍️ Validación</h2>
            <FirmaPad onSave={(firma) => handleServerChange('firma_responsable', firma)} onClear={() => handleServerChange('firma_responsable', null)} titulo="Firma del Responsable *" />
            <button onClick={guardarLocal} disabled={enviando} className="w-full mt-5 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 text-white font-black py-3.5 rounded-xl shadow-lg active:scale-95 transition-all text-xs tracking-widest uppercase cursor-pointer border border-cyan-400 disabled:opacity-50">
              {enviando ? 'Guardando...' : 'Guardar Check-in Diario'}
            </button>
          </div>
        </div>

      </div>

      {/* ALERTAS */}
      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}